import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Globe, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  cardClass,
  borderGrey,
  primaryText,
  secondaryText,
  tertiaryText,
  semiBoldText,
} from '../../../../styling/stylingHelper';
import type { ScopeClient } from '../../../../services/admin/cloudAgentService';
import type { CloudAgentScope } from '../../../../services/admin/cloudAgentTypes';
import { scopeForSelection, seedSelection } from '../../../../services/admin/cloudAgentScope';

interface ScopePickerProps {
  clients: ScopeClient[];
  /** Restaurants covered by the synthetic "All restaurants" scope. */
  allRestaurantsCount: number;
  scopesError: boolean;
  scope: CloudAgentScope | null;
  scopeLabel: string;
  /** Fires with the new scope AND the label to show in the pill. */
  onScopeChange: (scope: CloudAgentScope, label: string) => void;
}

/**
 * The scope pill + dropdown: one restaurant, an explicit subset across any clients, or every
 * restaurant. The checked set is deliberately independent of the browsed client, so the admin
 * can tick restaurants in one client, switch clients, and keep ticking — nothing commits until
 * "Apply", because committing would end the current conversation.
 */
export const ScopePicker: React.FC<ScopePickerProps> = ({
  clients,
  allRestaurantsCount,
  scopesError,
  scope,
  scopeLabel,
  onScopeChange,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const isAll = scope?.mode === 'all';
  // `n`, not `count` — `count` would send i18next looking for plural-suffixed keys.
  const allLabel = t('cloudAgent.scope.allRestaurants', { n: allRestaurantsCount });

  /** Draft selection, re-seeded from the live scope every time the dropdown opens. */
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  useEffect(() => {
    if (open) setCheckedIds(seedSelection(scope));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Which client's restaurants the list shows: an explicit pick, else the owner of the first
  // checked restaurant, else the first client.
  const owningClientId = useMemo(
    () => clients.find((c) => c.restaurants.some((r) => checkedIds.includes(r.restaurantId)))?.clientId ?? '',
    [clients, checkedIds]
  );
  const [browseClientId, setBrowseClientId] = useState('');
  const activeClientId = browseClientId || owningClientId || clients[0]?.clientId || '';
  const activeClient = clients.find((c) => c.clientId === activeClientId) ?? null;
  const activeRestaurants = activeClient?.restaurants ?? [];

  const allOfClientChecked =
    activeRestaurants.length > 0 &&
    activeRestaurants.every((r) => checkedIds.includes(r.restaurantId));

  const toggleRestaurant = (restaurantId: string) =>
    setCheckedIds((prev) =>
      prev.includes(restaurantId) ? prev.filter((id) => id !== restaurantId) : [...prev, restaurantId]
    );

  /** Check or uncheck this client's restaurants only — other clients' picks are untouched. */
  const toggleClient = () =>
    setCheckedIds((prev) => {
      const ids = activeRestaurants.map((r) => r.restaurantId);
      return allOfClientChecked
        ? prev.filter((id) => !ids.includes(id))
        : [...prev, ...ids.filter((id) => !prev.includes(id))];
    });

  /** The owning client doubles as the restaurant's user id (single-restaurant mode only). */
  const resolveUserId = (restaurantId: string) =>
    clients.find((c) => c.restaurants.some((r) => r.restaurantId === restaurantId))?.clientId;

  const restaurantName = (restaurantId: string) =>
    clients
      .flatMap((c) => c.restaurants)
      .find((r) => r.restaurantId === restaurantId)?.restaurantName ?? '';

  /** Checked restaurants NOT in the client currently being browsed — otherwise invisible. */
  const otherClientPicks = checkedIds
    .filter((id) => !activeRestaurants.some((r) => r.restaurantId === id))
    .map((id) => ({ restaurantId: id, restaurantName: restaurantName(id) }))
    .filter((pick) => pick.restaurantName !== '');

  /**
   * Only offer a total for the `all` collapse when the checkable list demonstrably IS the whole
   * universe. If the server's count ever exceeds what the client list holds, passing 0 means a
   * full tick-through stays `selected` rather than silently widening to every restaurant.
   */
  const checkableTotal = clients.reduce((sum, c) => sum + c.restaurants.length, 0);
  const collapseTotal = checkableTotal === allRestaurantsCount ? checkableTotal : 0;

  const apply = () => {
    const next = scopeForSelection(checkedIds, resolveUserId, collapseTotal);
    if (!next) return;
    const label =
      next.mode === 'restaurant'
        ? restaurantName(next.restaurantId)
        : next.mode === 'all'
          ? allLabel
          : t('cloudAgent.scope.nRestaurants', { n: new Set(checkedIds).size });
    onScopeChange(next, label);
    setOpen(false);
  };

  const selectClass = cn(
    'rounded-lg border px-3 py-2 text-sm w-full',
    borderGrey,
    'bg-background dark:bg-background-dark',
    primaryText,
    'focus:outline-none focus:ring-2 focus:ring-purple-400 dark:focus:ring-purple-400'
  );

  const pillLabel = scopesError
    ? t('cloudAgent.scopesError')
    : scopeLabel || t('cloudAgent.restaurant');

  return (
    <div className="relative max-w-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition',
          'max-w-full',
          borderGrey,
          'hover:bg-purple-50 dark:hover:bg-purple-900/20',
          secondaryText
        )}
      >
        {(isAll || scope?.mode === 'selected') && <Globe className="w-3.5 h-3.5" />}
        <span className="truncate max-w-[10rem] sm:max-w-none">{pillLabel}</span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className={cn(
              cardClass,
              'absolute right-0 mt-2 z-20 w-full sm:w-72 p-3 flex flex-col gap-3 shadow-lg border',
              borderGrey
            )}
          >
            {/* All-restaurants scope — commits immediately; it is its own mode, not a selection. */}
            <button
              type="button"
              onClick={() => {
                onScopeChange({ mode: 'all' }, allLabel);
                setOpen(false);
              }}
              className={cn(
                'flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-left transition',
                isAll
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                  : cn(primaryText, 'hover:bg-purple-50 dark:hover:bg-purple-900/20')
              )}
            >
              <span className="flex items-center gap-2 min-w-0">
                <Globe className="w-4 h-4 shrink-0" />
                <span className="truncate">{allLabel}</span>
              </span>
              {isAll && <Check className="w-4 h-4 shrink-0" />}
            </button>

            <div className={cn('border-t', borderGrey)} />

            <div className="flex flex-col gap-1">
              <label className={cn(tertiaryText, 'text-[11px] font-medium')}>
                {t('cloudAgent.client')}
              </label>
              <select
                className={selectClass}
                value={activeClientId}
                onChange={(e) => setBrowseClientId(e.target.value)}
              >
                {clients.length === 0 && <option value="">{t('cloudAgent.noScopes')}</option>}
                {clients.map((c) => (
                  <option key={c.clientId} value={c.clientId}>
                    {c.clientName || c.clientEmail}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <label className={cn(tertiaryText, 'text-[11px] font-medium')}>
                  {t('cloudAgent.restaurant')}
                </label>
                <span className="flex items-center gap-2">
                  {/* Running total across ALL clients, so an off-screen pick stays counted. */}
                  <span className={cn(tertiaryText, 'text-[11px]')}>
                    {t('cloudAgent.scope.nSelected', { n: checkedIds.length })}
                  </span>
                  {checkedIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setCheckedIds([])}
                      className="text-[11px] text-purple-600 dark:text-purple-300 hover:underline"
                    >
                      {t('cloudAgent.scope.clear')}
                    </button>
                  )}
                </span>
              </div>

              {/* Picks belonging to OTHER clients: their checkboxes are off-screen while this
                  client is browsed, so they are surfaced here as removable chips. Nothing can be
                  in the selection without being visible — the bug this replaces was a restaurant
                  silently riding along from the client the admin started on. */}
              {otherClientPicks.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span className={cn(tertiaryText, 'text-[11px]')}>
                    {t('cloudAgent.scope.alsoSelected')}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {otherClientPicks.map((pick) => (
                      <span
                        key={pick.restaurantId}
                        className={cn(
                          'flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] max-w-full',
                          'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        )}
                      >
                        <span className="truncate">{pick.restaurantName}</span>
                        <button
                          type="button"
                          onClick={() => toggleRestaurant(pick.restaurantId)}
                          aria-label={t('cloudAgent.scope.removeRestaurant', {
                            name: pick.restaurantName,
                          })}
                          className="shrink-0 hover:opacity-70"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {activeRestaurants.length > 0 && (
                <button
                  type="button"
                  onClick={toggleClient}
                  className={cn(
                    'text-left text-[11px] px-1 py-0.5 rounded transition',
                    'text-purple-600 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                  )}
                >
                  {allOfClientChecked
                    ? t('cloudAgent.scope.deselectAllOf', {
                        client: activeClient?.clientName || activeClient?.clientEmail || '',
                      })
                    : t('cloudAgent.scope.selectAllOf', {
                        client: activeClient?.clientName || activeClient?.clientEmail || '',
                      })}
                </button>
              )}

              {/* Scrolls in place so a long client never pushes Apply off a phone screen. */}
              <div className={cn('max-h-64 overflow-y-auto rounded-lg border', borderGrey)}>
                {activeRestaurants.length === 0 ? (
                  <p className={cn(tertiaryText, 'text-xs p-3')}>{t('cloudAgent.noScopes')}</p>
                ) : (
                  activeRestaurants.map((r) => {
                    const checked = checkedIds.includes(r.restaurantId);
                    return (
                      <label
                        key={r.restaurantId}
                        className={cn(
                          'flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition',
                          'hover:bg-purple-50 dark:hover:bg-purple-900/20',
                          checked ? 'text-purple-700 dark:text-purple-300' : primaryText
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRestaurant(r.restaurantId)}
                          className="w-4 h-4 shrink-0 accent-purple-500 dark:accent-purple-400"
                        />
                        <span className="truncate">{r.restaurantName}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            {/* Nothing commits until Apply — a scope change ends the current conversation. */}
            <button
              type="button"
              onClick={apply}
              disabled={checkedIds.length === 0}
              className={cn(
                'rounded-lg px-3 py-2 text-sm transition',
                semiBoldText,
                checkedIds.length > 0
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:opacity-90'
                  : 'bg-gray-100 dark:bg-gray-700 text-textTertiary dark:text-textTertiary-dark cursor-not-allowed'
              )}
            >
              {t('cloudAgent.scope.apply')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ScopePicker;
